import { useSsrCoreApp } from "./ssrControl.mjs";
import { CountryCodeEnum } from "../graphql/graphql-types.gen.mjs";
import {
  CreateAddressDocument,
  CustomerAddressesDocument,
  DeleteAddressDocument,
  UpdateAddressDocument
} from "../graphql/Address.mjs";
import {
  CitiesForRegionDocument,
  RegionsForCountryDocument
} from "../graphql/Country.mjs";
import { toNonNullable } from "../utils/collections.mjs";
import { AUTH_USER, CONFIG_STORE_COUNTRY } from "../utils/provides.mjs";
import { FETCHING_ADDRESS_ERROR } from "../types/errors.mjs";
import { isString } from "lodash-es";
import { useMutation, useQuery } from "villus";
import { computed, inject, reactive, ref } from "vue";
function useFetchCreateAddress() {
  const { execute, isFetching } = useMutation(CreateAddressDocument);
  return {
    execute,
    isFetching
  };
}
export function useCreateAddress(_useFetchCreateAddress = useFetchCreateAddress) {
  const { execute, isFetching } = _useFetchCreateAddress();
  async function createAddress(input) {
    const { data, error } = await execute({ input });
    if (error) {
      throw new Error(error.message);
    }
    return data?.response;
  }
  return {
    createAddress,
    isCreating: isFetching
  };
}
function useFetchUpdateAddress() {
  const { execute, isFetching } = useMutation(UpdateAddressDocument);
  return {
    execute,
    isFetching
  };
}
export function useUpdateAddress(_useFetchUpdateAddress = useFetchUpdateAddress) {
  const { execute, isFetching } = _useFetchUpdateAddress();
  async function updateAddress(id, input) {
    const { data, error } = await execute({ id, input });
    if (error) {
      throw new Error(error.message);
    }
    return data?.response;
  }
  return {
    updateAddress,
    isFetching
  };
}
export function useFetchRegions(countryId) {
  const {
    execute,
    isFetching,
    data: regions
  } = useQuery({
    query: RegionsForCountryDocument.toString(),
    variables: { countryId }
  });
  return {
    regions,
    execute,
    isFetching
  };
}
export function useFetchCities(selectedRegion) {
  const { isFetching, data: cities } = useQuery({
    query: CitiesForRegionDocument.toString(),
    variables: computed(() => {
      if (!selectedRegion.value) {
        return {
          regionId: ""
        };
      }
      return {
        regionId: String(selectedRegion.value?.id)
      };
    }),
    fetchOnMount: false
  });
  return {
    isFetching,
    cities: computed(() => toNonNullable(cities.value?.region?.cities) ?? [])
  };
}
export function useCountry(_useFetchRegions = useFetchRegions, _useFetchCities) {
  const countryId = inject(CONFIG_STORE_COUNTRY, CountryCodeEnum.Eg);
  const selectedRegion = ref(null);
  const { regions, isFetching: isFetchingRegions } = _useFetchRegions(countryId);
  const { cities, isFetching: isFetchingCities } = _useFetchCities?.(selectedRegion) || useFetchCities(selectedRegion);
  return {
    selectedRegion,
    regions: computed(() => {
      return [null].concat(regions?.value?.country?.regions || []);
    }),
    cities: computed(() => {
      return [null, ...cities?.value?.length ? cities.value : []];
    }),
    isFetchingCities,
    isFetchingRegions
  };
}
function fetchCustomerAddresses(onMount = true) {
  const { data, isFetching, error, execute } = useQuery({
    query: CustomerAddressesDocument.toString(),
    cachePolicy: "network-only",
    fetchOnMount: onMount
  });
  return {
    data,
    isFetching,
    error,
    execute
  };
}
export function useCustomerAddresses(opts) {
  const { waitForPageToBeLoaded } = useSsrCoreApp();
  const { isFetching, execute } = useQuery({
    query: CustomerAddressesDocument.toString(),
    cachePolicy: "network-only",
    fetchOnMount: false
  });
  const addresses = ref([]);
  const isFetchingAddresses = ref(true);
  async function fetchAddresses() {
    isFetchingAddresses.value = true;
    await waitForPageToBeLoaded();
    try {
      const { data, error } = await execute();
      if (error) {
        throw new Error(error.message);
      }
      addresses.value = toNonNullable(data?.customer?.addresses).sort(
        (a, b) => Number(b.default_shipping) - Number(a.default_shipping)
      );
      return addresses.value;
    } catch (e) {
      console.log(e);
      if (/The current customer isn't authorized\./.test(e.message)) {
        throw new Error(FETCHING_ADDRESS_ERROR.NOT_AUTHORIZED.toString());
      }
      throw e;
    } finally {
      isFetchingAddresses.value = false;
    }
  }
  if (opts?.fetchOnMount)
    onMounted(async () => {
      await waitForPageToBeLoaded();
      execute();
    });
  return {
    addresses,
    isFetching,
    fetchAddresses
  };
}
export function useAddressForm(initialValue) {
  const user = inject(AUTH_USER);
  const countryCode = inject(CONFIG_STORE_COUNTRY, CountryCodeEnum.Eg);
  const fullName = `${initialValue?.firstname || user?.value?.firstname || ""} ${initialValue?.lastname || user?.value?.lastname || ""}`;
  const addressInput = reactive({
    fullName,
    phone: initialValue?.telephone || "",
    street: initialValue?.street && initialValue?.street[0] || "",
    buildingNo: initialValue?.building || "",
    floor: initialValue?.floor || "",
    apt: initialValue?.apartment || "",
    notes: "",
    city: initialValue?.city || null,
    postcode: initialValue?.postcode || "",
    region: initialValue?.region ? {
      name: initialValue.region.region,
      code: initialValue.region.region_code,
      id: initialValue.region.region_id
    } : null,
    landmark: initialValue?.landmark || "",
    title: initialValue?.title || "",
    isDefault: initialValue?.default_billing || false
  });
  function toAddressInput() {
    return {
      telephone: addressInput.phone,
      street: [addressInput.street],
      firstname: addressInput.fullName.trim().split(" ")[0],
      lastname: addressInput.fullName.trim().split(" ").slice(1).join(" "),
      country_code: countryCode,
      city: addressInput.city?.code,
      postcode: addressInput.postcode,
      default_billing: addressInput.isDefault,
      default_shipping: addressInput.isDefault,
      region: {
        region: addressInput.region?.name,
        region_code: addressInput.region?.code,
        region_id: addressInput.region?.id
      },
      landmark: addressInput.landmark,
      floor: addressInput.floor,
      building: addressInput.buildingNo,
      apartment: addressInput.apt,
      title: addressInput.title
    };
  }
  return {
    toAddressInput,
    formInput: addressInput
  };
}
function fetchDeleteAddress() {
  const { execute, isFetching } = useMutation(DeleteAddressDocument);
  return {
    execute,
    isFetching
  };
}
export function useDeleteAddress(_fetchDeleteAddress = fetchDeleteAddress, _useFetchUpdateAddress = useFetchUpdateAddress) {
  const { execute, isFetching } = _fetchDeleteAddress();
  const { updateAddress } = useUpdateAddress(_useFetchUpdateAddress);
  async function deleteAddress(address) {
    if (address.default_shipping || address.default_billing) {
      await updateAddress(address.id, {
        default_billing: false,
        default_shipping: false
      });
    }
    const { data, error } = await execute({ id: address.id });
    if (error) {
      throw new Error(error.message);
    }
    return data?.response;
  }
  return {
    deleteAddress,
    isFetching
  };
}
export function mapAddressFormat(address) {
  const fragments = [
    address.building,
    address.street?.[0],
    isString(address.region) ? address.region : address?.region?.region,
    address.city?.name,
    address.country?.name,
    address.floor ? `Floor ${address.floor}` : "",
    address.apartment ? `${address.apartment} Apartment` : ""
  ].filter((fragment) => fragment);
  return fragments.join(",");
}
export function toCartAddressInput(customerAddress) {
  return {
    telephone: customerAddress.telephone,
    street: customerAddress.street || [],
    firstname: customerAddress.firstname || "",
    lastname: customerAddress.lastname || "",
    country_code: customerAddress.country_code,
    city: customerAddress.city || "",
    postcode: customerAddress.postcode,
    region: customerAddress.region?.region_code,
    landmark: customerAddress.landmark || "",
    floor: customerAddress.floor || "",
    building: customerAddress.building || "",
    apartment: customerAddress.apartment || "",
    title: customerAddress.title || ""
  };
}
export function isIdenticalAddresses(a, b) {
  if (!a || !b) return false;
  return a?.street?.[0] === b?.street && a.telephone === b.phone;
}
export function isIdenticalOrderAddresses(a, b) {
  return a?.street?.[0] === b.address && a.telephone === b.phone_number;
}
